<script setup lang="ts">
import type { AvailabilityData, AvailabilitySummary } from '~/composables/useTicketAvailability'
import { computed, ref, watch } from 'vue'
import { buildAvailabilitySummary } from '~/composables/useTicketAvailability'
import { logApiError } from '~/utils/logger'

interface CampPreview {
  id: string
  title: string
  description?: string | null
  price?: number | null
  priceLabel?: string | null
  weekPrice?: number | null
  duration?: string | null
  start_date: string
  end_date: string
  location?: string | null
  status?: string | null
  max_attendees?: number | null
  current_attendees?: number | null
  show_available_tickets?: boolean | null
}

const { getCamps } = useCamps()
const { canManageCamps, canAccessAdmin, canManageFinance, hasAnyRole } = usePermissions()

const camps = ref<CampPreview[]>([])
const loadingCamps = ref(false)
const availabilityByCampId = ref<Record<string, AvailabilitySummary>>({})

const canViewConceptCamps = computed(() => canManageCamps.value || canAccessAdmin.value)

const canViewAvailabilityCounts = computed(() => {
  if (canManageCamps.value || canAccessAdmin.value || canManageFinance.value)
    return true

  return hasAnyRole(['admin', 'godmode', 'monitor', 'accountant', 'manager'])
})

const canShowAvailabilityCounts = (camp: CampPreview) => {
  return Boolean(camp.show_available_tickets) || canViewAvailabilityCounts.value
}

const visibleCamps = computed<CampPreview[]>(() => {
  let result = camps.value

  if (!canViewConceptCamps.value)
    result = result.filter(camp => camp.status !== 'draft')

  // Sort chronologically by start date (earliest first)
  return result.sort((a, b) => {
    const dateA = new Date(a.start_date || '')
    const dateB = new Date(b.start_date || '')

    return dateA.getTime() - dateB.getTime()
  })
})

const upcomingCamps = computed(() => {
  return visibleCamps.value.slice(0, 3)
})

const loadAvailabilitySummaries = async (campsToCheck: CampPreview[]) => {
  const candidates = campsToCheck.filter(camp => {
    if (camp.max_attendees === null || camp.max_attendees === undefined)
      return false
    if (camp.current_attendees === null || camp.current_attendees === undefined)
      return false

    return camp.max_attendees - (camp.current_attendees || 0) <= 0
  })

  const missing = candidates.filter(camp => !availabilityByCampId.value[String(camp.id)])

  if (!missing.length)
    return

  await Promise.all(missing.map(async camp => {
    try {
      const response = await $fetch<{ success: boolean, data: AvailabilityData }>(`/api/camps/${camp.id}/availability`)

      if (response.success) {
        availabilityByCampId.value = {
          ...availabilityByCampId.value,
          [String(camp.id)]: buildAvailabilitySummary(response.data),
        }
      }
    }
    catch (error) {
      logApiError(`/api/camps/${camp.id}/availability`, error)
    }
  }))
}

const getAvailabilitySummary = (campId: string | number) => availabilityByCampId.value[String(campId)]

const getAvailabilityLabel = (camp: CampPreview) => {
  const summary = getAvailabilitySummary(camp.id)
  const showCounts = canShowAvailabilityCounts(camp)

  // Use availability summary if available (more accurate calculation)
  if (summary) {
    if (summary.soldOut)
      return 'Volzet'
    if (summary.limited)
      return 'Beperkte beschikbaarheid'
    if (!showCounts)
      return 'Plaatsen beschikbaar'
    if (summary.effectiveRemaining === 1)
      return '1 plaats beschikbaar'

    return `${summary.effectiveRemaining} plaatsen beschikbaar`
  }

  // Fallback to simple calculation if summary not loaded yet
  const remaining = (camp.max_attendees || 0) - (camp.current_attendees || 0)

  if (remaining <= 0)
    return 'Volzet'
  if (!showCounts)
    return 'Plaatsen beschikbaar'
  if (remaining === 1)
    return '1 plaats beschikbaar'

  return `${remaining} plaatsen beschikbaar`
}

const getAvailabilityTone = (camp: CampPreview) => {
  const summary = getAvailabilitySummary(camp.id)

  if (summary) {
    if (summary.soldOut)
      return 'error'
    if (summary.limited)
      return 'warning'
    if (summary.effectiveRemaining <= 5)
      return 'error'
    if (summary.effectiveRemaining <= 10)
      return 'warning'

    return 'success'
  }

  // Fallback
  const remaining = (camp.max_attendees || 0) - (camp.current_attendees || 0)

  if (remaining <= 0)
    return 'error'

  return 'warning'
}

const loadCamps = async () => {
  loadingCamps.value = true
  try {
    const filters = {
      limit: 3,
      sortBy: 'start_date',
      sortOrder: 'asc' as const,
      ...(canViewConceptCamps.value ? {} : { status: 'published' as const }),
    }

    const response = await getCamps(filters)

    camps.value = (response.data || []) as CampPreview[]
    await loadAvailabilitySummaries(camps.value)
  }
  catch (error) {
    logApiError('/api/camps', error, { operation: 'loadCamps', limit: 3 })
  }
  finally {
    loadingCamps.value = false
  }
}

watch(
  () => canViewConceptCamps.value,
  () => {
    loadCamps()
  },
  { immediate: true },
)
</script>

<template>
  <div id="camps" class="camps-preview-section pb-16 pt-8" style="background-color: #f26f63; color: #f6ebd7">
    <!-- Decorative background gears -->
    <div class="gear-decoration gear-1" />
    <div class="gear-decoration gear-2" />
    <div class="gear-decoration gear-3" />
    <div class="gear-decoration gear-4" />

    <VContainer>
      <VRow align="start">
        <!-- Left: Information about camps -->
        <VCol cols="12" md="5" class="mb-8 mb-md-0">
          <div class="camps-info-container">
            <div class="section-badge">
              Onze Kampen
            </div>
            <h2 class="section-title">
              Kampen vol avontuur en plezier
            </h2>
            <p class="section-description">
              Elk kamp is zorgvuldig samengesteld met een gevarieerd programma vol leuke activiteiten, sportieve
              uitdagingen en creatieve workshops.
            </p>
            <p class="section-description">
              Van teamspellen tot creatieve knutselactiviteiten: elke dag zit boordevol verrassingen, afgestemd op de verschillende leeftijdsgroepen.
            </p>
          </div>
        </VCol>

        <!-- Right: Vertical listing of camps -->
        <VCol cols="12" md="7">
          <div class="camps-list-container">
            <div class="d-flex justify-space-between align-center mb-6">
              <h3 class="list-title mb-0">
                Aankomende kampen
              </h3>
              <VBtn to="/kampen" color="primary" size="default">
                Bekijk alle kampen
                <VIcon icon="bx-right-arrow-alt" end />
              </VBtn>
            </div>

            <div v-if="loadingCamps" class="text-center py-12">
              <VProgressCircular indeterminate color="primary" size="48" />
            </div>

            <div v-else-if="upcomingCamps.length === 0" class="text-center py-12">
              <VIcon icon="bx-calendar-x" size="64" color="grey" class="mb-4" />
              <p class="text-h6 text-medium-emphasis">
                Er zijn momenteel geen kampen beschikbaar.
              </p>
            </div>

            <VCard v-for="camp in upcomingCamps" :key="camp.id" class="camp-preview-card mb-4" elevation="0">
              <VCardText class="pa-5">
                <VRow align="center">
                  <VCol cols="12" sm="8">
                    <div class="d-flex align-center gap-2 mb-1">
                      <h3 class="camp-title mb-0">
                        {{ camp.title }}
                      </h3>
                      <VChip
                        v-if="camp.status === 'draft' && canViewConceptCamps"
                        size="small"
                        color="warning"
                        variant="tonal"
                      >
                        Concept
                      </VChip>
                      <VChip
                        v-if="new Date(camp.end_date) < new Date() && canViewConceptCamps"
                        size="small"
                        color="secondary"
                        variant="tonal"
                      >
                        Afgelopen
                      </VChip>
                    </div>
                    <p v-if="camp.description" class="text-body-2 text-medium-emphasis mb-3">
                      {{ camp.description.substring(0, 100) }}{{ camp.description.length > 100 ? '...' : '' }}
                    </p>

                    <div class="d-flex flex-wrap gap-2 mb-2">
                      <VChip size="small" color="primary" variant="tonal">
                        <VIcon icon="bx-calendar" start size="16" />
                        {{
                          new Date(camp.start_date).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })
                        }}
                        -
                        {{
                          new Date(camp.end_date).toLocaleDateString('nl-BE', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        }}
                      </VChip>

                      <VChip v-if="camp.start_date && camp.end_date" size="small" color="secondary" variant="tonal">
                        <VIcon icon="bx-time" start size="16" />
                        {{
                          Math.ceil(
                            (new Date(camp.end_date).getTime() - new Date(camp.start_date).getTime())
                              / (1000 * 60 * 60 * 24),
                          )
                        }}
                        dagen
                      </VChip>

                      <VChip v-if="camp.location" size="small" color="info" variant="tonal">
                        <VIcon icon="bx-map" start size="16" />
                        {{ camp.location }}
                      </VChip>
                    </div>

                    <div v-if="camp.max_attendees && camp.current_attendees !== null" class="mt-2">
                      <div class="d-flex align-center gap-2">
                        <VIcon
                          icon="bx-user"
                          size="18"
                          :color="getAvailabilityTone(camp)"
                        />
                        <span
                          class="text-body-2"
                          :class="getAvailabilityTone(camp) === 'error' ? 'text-error' : getAvailabilityTone(camp) === 'warning' ? 'text-warning' : ''"
                        >
                          {{ getAvailabilityLabel(camp) }}
                        </span>
                      </div>
                    </div>
                  </VCol>

                  <VCol cols="12" sm="4" class="text-center text-sm-end">
                    <div v-if="camp.price !== null && camp.price !== undefined" class="mb-3">
                      <div class="text-h4 font-weight-bold" :class="camp.price === 0 ? 'text-error' : 'text-primary'">
                        {{ camp.price === 0 ? 'Volzet' : `€${camp.price}` }}{{ camp.priceLabel || '' }}
                      </div>
                      <!-- Show week price if available (when there are both day and week tickets) -->
                      <div v-if="camp.weekPrice" class="text-caption text-medium-emphasis">
                        €{{ camp.weekPrice }} / {{ Math.ceil((new Date(camp.end_date).getTime() - new Date(camp.start_date).getTime()) / (1000 * 60 * 60 * 24)) }} dagen
                      </div>
                      <!-- Show duration label for week-only tickets -->
                      <div v-else-if="!camp.priceLabel" class="text-caption text-medium-emphasis">
                        voor
                        {{
                          camp.duration || (camp.start_date && camp.end_date
                            ? Math.ceil(
                              (new Date(camp.end_date).getTime() - new Date(camp.start_date).getTime())
                                / (1000 * 60 * 60 * 24),
                            )
                            : '')
                        }}
                        dagen
                      </div>
                    </div>

                    <VBtn :to="{ path: '/camp-details', query: { id: camp.id } }" color="primary" block>
                      Meer info
                    </VBtn>
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>
          </div>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.camps-preview-section {
  position: relative;
  overflow: hidden;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  background: rgba(246, 235, 215, 20%);
  color: #f6ebd7;
  font-size: 1rem;
  font-weight: 600;
  gap: 0.5rem;
  letter-spacing: 0.5px;
  margin-block-end: 1rem;
  padding-block: 0.5rem;
  padding-inline: 1.5rem;
  text-transform: uppercase;
}

.section-title {
  color: #f6ebd7;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  line-height: 1.2;
  margin-block-end: 1.5rem;
}

.section-description {
  color: #f6ebd7;
  font-size: 1.125rem;
  line-height: 1.6;
  margin-block-end: 1rem;
  opacity: 0.95;
}

.list-title {
  color: #f6ebd7;
  font-size: 1.5rem;
  font-weight: 700;
}

.gear-decoration {
  position: absolute;
  border-radius: 50%;
  opacity: 0.05;
  pointer-events: none;
}

.gear-1 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    50px 50px;
  background-size: 50px 50px;
  block-size: 300px;
  inline-size: 300px;
  inset-block-start: 10%;
  inset-inline-start: -5%;
}

.gear-2 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    35px 35px;
  background-size: 35px 35px;
  block-size: 200px;
  inline-size: 200px;
  inset-block-start: 60%;
  inset-inline-end: 10%;
}

.gear-3 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    25px 25px;
  background-size: 25px 25px;
  block-size: 150px;
  inline-size: 150px;
  inset-block-end: 20%;
  inset-inline-start: 15%;
}

.gear-4 {
  background:
    radial-gradient(circle, #f6ebd7 20%, transparent 20%),
    radial-gradient(circle, transparent 20%, #f6ebd7 20%, #f6ebd7 30%, transparent 30%);
  background-position:
    0 0,
    30px 30px;
  background-size: 30px 30px;
  block-size: 180px;
  inline-size: 180px;
  inset-block-start: 40%;
  inset-inline-end: -3%;
}

.camps-info-container {
  position: relative;
  z-index: 1;
}

.camps-list-container {
  position: relative;
  z-index: 1;
}

.camp-preview-card {
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 8%);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 12%) !important;
    transform: translateY(-4px);
  }
}

.camp-title {
  display: block;
  color: #2c3e50 !important;
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  line-height: 1.2;
  margin-block-end: 0.5rem;
}
</style>
